/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  Input,
  List,
  ListItem,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { fetchSuggestions } from "../actions/location.actions";

const InputWithSuggestionSimple = ({
  name,
  placeholder,
  value = "",
  tripType = "",
  filter,
  onChange,
}) => {
  const [capturedValue, setCapturedValue] = useState(value);
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isSuggestionVisible, setIsSuggestionVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // -----------------------------
  // Load recent searches
  // -----------------------------
  useEffect(() => {
    const stored =
      JSON.parse(localStorage.getItem(`${name}_${tripType}_${filter}`)) || [];
    setRecentSearches(stored);
  }, [name, tripType, filter]);

  // Sync internal state with external value prop changes
  useEffect(() => {
    if (value !== undefined && value !== capturedValue) {
      setCapturedValue(value);
    }
  }, [value]);

  // -----------------------------
  // Input Change
  // -----------------------------
  const handleInputChange = (input) => {
    setCapturedValue(input);
    if (onChange) onChange(input);
  };

  // -----------------------------
  // Fetch Suggestions
  // -----------------------------
  const getSuggestionsFromServer = async (input) => {
    setLoading(true);
    try {
      const data = await fetchSuggestions(input, filter);
      setResults(data?.data || []);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Handle Selection
  // -----------------------------
  const handleSelect = (item) => {
    const value = item.description;

    setCapturedValue(value);
    if (onChange) onChange(value);

    setResults([]);
    setIsSuggestionVisible(false);

    const updatedSearches = [
      value,
      ...recentSearches.filter((s) => s !== value),
    ].slice(0, 3);

    setRecentSearches(updatedSearches);
    localStorage.setItem(
      `${name}_${tripType}_${filter}`,
      JSON.stringify(updatedSearches),
    );
  };

  // -----------------------------
  // Debounce Search
  // -----------------------------
  useEffect(() => {
    if (!capturedValue) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      getSuggestionsFromServer(capturedValue);
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [capturedValue]);

  // -----------------------------
  // Close on Outside Click (KEY FIX)
  // -----------------------------
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsSuggestionVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <FormControl>
      <Box position="relative" ref={wrapperRef}>
        <Input
          value={capturedValue}
          name={name}
          placeholder={placeholder}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setIsSuggestionVisible(true)}
          autoComplete="off"
          isRequired
        />

        {isSuggestionVisible &&
          (results.length > 0 || recentSearches.length > 0) && (
            <Box
              bg="white"
              boxShadow="md"
              mt={1}
              p={2}
              borderRadius="md"
              zIndex={10}
              maxH="350px"
              overflowY="auto"
              border="1px solid"
              borderColor="gray.200"
              position="absolute"
              w="100%"
            >
              <List spacing={1}>
                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <>
                    <ListItem fontWeight="bold" p={2} color="gray.500">
                      Recent Searches
                    </ListItem>
                    {recentSearches.map((search, index) => (
                      <ListItem
                        key={`recent-${index}`}
                        p={2}
                        _hover={{ bg: "gray.100", cursor: "pointer" }}
                        onClick={() => handleSelect({ description: search })}
                      >
                        {search}
                      </ListItem>
                    ))}
                  </>
                )}

                {/* Loading */}
                {loading && (
                  <ListItem p={2}>
                    <Spinner size="sm" />
                  </ListItem>
                )}

                {/* Results */}
                {results.length > 0 && (
                  <>
                    <ListItem fontWeight="bold" p={2} color="gray.500">
                      Results
                    </ListItem>
                    {results.map((result, index) => (
                      <ListItem
                        key={`result-${index}`}
                        p={2}
                        _hover={{ bg: "gray.100", cursor: "pointer" }}
                        onClick={() => handleSelect(result)}
                      >
                        {result.description}
                      </ListItem>
                    ))}
                  </>
                )}
              </List>
            </Box>
          )}
      </Box>
    </FormControl>
  );
};

export default InputWithSuggestionSimple;
